import * as Papa from 'papaparse/papaparse.js';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import dataService from './dataService';

class ExportService {
  // Exportar para CSV
  exportToCSV(data, filename = 'ordens_servico') {
    const csvData = data.map(order => ({
      'Número da Ordem': dataService.safeValue(order['Ord.Ser']),
      'Data de Inclusão': dataService.formatDate(order['Data Inclusao']),
      'Cliente': dataService.safeValue(order['Cliente']),
      'Descrição': dataService.safeValue(order['Descrição']),
      'Problema': dataService.safeValue(order['Problema']),
      'Status': dataService.safeValue(order['Posição']),
      'Técnico': dataService.safeValue(order['Técnico']),
      'Atendente': dataService.safeValue(order['Atendente']),
      'Faturada': dataService.safeValue(order['Faturada']),
      'Cobertura': dataService.safeValue(order['Cobertura']),
      'Valor Serviço': order['Vlr.cobrado ser'] ? 
        dataService.formatCurrency(order['Vlr.cobrado ser']).replace('R$ ', '') : '0,00',
      'Valor Material': order['Vlr.cobrado mat'] ? 
        dataService.formatCurrency(order['Vlr.cobrado mat']).replace('R$ ', '') : '0,00',
      'Local de Entrega': dataService.safeValue(order['Local entrega']),
      'Marca/Modelo': dataService.safeValue(order['Marca/modelo']),
      'Fabricante': dataService.safeValue(order['Fabricante'])
    }));

    const csv = Papa.unparse(csvData, {
      delimiter: ';',
      header: true,
      encoding: 'utf-8'
    });

    // Adicionar BOM para compatibilidade com Excel
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csv;
    
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  // Exportar para PDF
  exportToPDF(data, filename = 'ordens_servico') {
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape para mais espaço
    
    // Configurações
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    
    // Título
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Relatório de Ordens de Serviço - Zaffari', margin, 20);
    
    // Data de geração
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const now = new Date().toLocaleString('pt-BR');
    doc.text(`Gerado em: ${now}`, margin, 28);
    doc.text(`Total de ordens: ${data.length}`, margin, 34);
    
    // Estatísticas básicas
    const stats = {
      concluidas: data.filter(order => order['Posição'] && order['Posição'].includes('Concluída')).length,
      faturadas: data.filter(order => order['Faturada'] && !order['Faturada'].includes('Não faturada')).length,
      valorTotalServicos: data.reduce((sum, order) => sum + (parseFloat(order['Vlr.cobrado ser']) || 0), 0),
      valorTotalMateriais: data.reduce((sum, order) => sum + (parseFloat(order['Vlr.cobrado mat']) || 0), 0)
    };
    
    doc.text(`Ordens concluídas: ${stats.concluidas}`, margin, 40);
    doc.text(`Ordens faturadas: ${stats.faturadas}`, margin + 60, 40);
    doc.text(`Valor total serviços: ${dataService.formatCurrency(stats.valorTotalServicos)}`, margin + 120, 40);
    doc.text(`Valor total materiais: ${dataService.formatCurrency(stats.valorTotalMateriais)}`, margin + 200, 40);
    
    // Preparar dados da tabela
    const tableColumns = [
      'Ordem',
      'Data',
      'Descrição',
      'Status',
      'Técnico',
      'Faturada',
      'Vlr. Serviço',
      'Vlr. Material'
    ];
    
    const tableRows = data.map(order => [
      dataService.safeValue(order['Ord.Ser']),
      dataService.formatDate(order['Data Inclusao']),
      this.truncateText(dataService.safeValue(order['Descrição']), 25),
      this.truncateText(dataService.safeValue(order['Posição']), 15),
      this.truncateText(dataService.safeValue(order['Técnico']), 20),
      order['Faturada'] && !order['Faturada'].includes('Não faturada') ? 'Sim' : 'Não',
      dataService.formatCurrency(order['Vlr.cobrado ser']),
      dataService.formatCurrency(order['Vlr.cobrado mat'])
    ]);
    
    // Criar tabela
    doc.autoTable({
      head: [tableColumns],
      body: tableRows,
      startY: 50,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [55, 65, 81], // gray-700
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [249, 250, 251] // gray-50
      },
      columnStyles: {
        0: { cellWidth: 20 }, // Ordem
        1: { cellWidth: 25 }, // Data
        2: { cellWidth: 50 }, // Descrição
        3: { cellWidth: 35 }, // Status
        4: { cellWidth: 40 }, // Técnico
        5: { cellWidth: 20 }, // Faturada
        6: { cellWidth: 30, halign: 'right' }, // Vlr. Serviço
        7: { cellWidth: 30, halign: 'right' }  // Vlr. Material
      },
      margin: { left: margin, right: margin },
      didDrawPage: function(data) {
        // Rodapé
        const pageNumber = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(`Página ${data.pageNumber} de ${pageNumber}`, 
               pageWidth - margin - 30, 
               doc.internal.pageSize.height - 10);
      }
    });
    
    // Salvar arquivo
    doc.save(`${filename}_${new Date().toISOString().split('T')[0]}.pdf`);
  }

  // Função auxiliar para truncar texto
  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength - 3) + '...';
  }

  // Exportar estatísticas do dashboard para PDF
  exportDashboardToPDF(stats, statusData, timelineData) {
    const doc = new jsPDF();
    const margin = 20;
    
    // Título
    doc.setFontSize(20);
    doc.text('Dashboard - Ordens de Serviço Zaffari', margin, 30);
    
    // Data
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    const now = new Date().toLocaleString('pt-BR');
    doc.text(`Relatório gerado em: ${now}`, margin, 45);
    
    // KPIs
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Indicadores Principais', margin, 65);
    
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    let yPos = 80;
    doc.text(`Total de Ordens: ${stats.totalOrdens}`, margin, yPos);
    yPos += 10;
    doc.text(`Ordens Concluídas: ${stats.ordensConcluidas}`, margin, yPos);
    yPos += 10;
    doc.text(`Valor Total Serviços: ${dataService.formatCurrency(stats.totalValorServico)}`, margin, yPos);
    yPos += 10;
    doc.text(`Valor Total Materiais: ${dataService.formatCurrency(stats.totalValorMaterial)}`, margin, yPos);
    
    // Status das Ordens
    yPos += 25;
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text('Distribuição por Status', margin, yPos);
    
    yPos += 15;
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    statusData.forEach(item => {
      doc.text(`${item.label}: ${item.value} ordens`, margin, yPos);
      yPos += 8;
    });
    
    
    
    if (yPos > 250) {
      doc.addPage();
      yPos = 30;
    }
    
    doc.save(`dashboard_zaffari_${new Date().toISOString().split('T')[0]}.pdf`);
  }
}

const exportServiceInstance = new ExportService();
export default exportServiceInstance; 
